import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { API_URL } from 'constants/environment';

import { setCredentials } from 'reduxLocal/slices/authSlice';

import { getAxiosInstance } from 'api/utils';

import { useAccessToken } from './context';
import { useLocalStorageRef } from './useLocalStorageRef';

/**
 * Buffer in milliseconds to subtract from actual expiration time
 */
const REFRESH_TOKEN_EXPIRATION_BUFFER = 30_000;

export const REFRESH_TOKEN_PATH = '/api/v1/authentication/token/refresh';

/**
 * Hook for managing the refreshing of the access token
 * The hook refreshes the access token based on the expiration time encoded in the access token
 */
export const useAuthenticationRefreshToken = () => {
  const dispatch = useDispatch();
  const { accessToken, getTokenExpirationTime, setAccessToken } =
    useAccessToken();

  const loggedInRef = useLocalStorageRef('isLoggedIn', false);

  /**
   * Refreshes the access token
   * @returns the new access token, or undefined if not signed in or the refresh failed
   */
  const refreshAccessToken = useCallback(async (): Promise<
    string | undefined
  > => {
    // Don't refresh if not signed in
    if (!loggedInRef.current.getStoredValue()) {
      return undefined;
    }

    try {
      const { data } = await getAxiosInstance().post<{ access_token: string }>(
        REFRESH_TOKEN_PATH,
        {},
        { baseURL: API_URL, withCredentials: true }
      );
      dispatch(setCredentials({ access_token: data.access_token }));
      setAccessToken(data.access_token);

      return data.access_token;
    } catch {
      loggedInRef.current.setStoredValue(false);

      return undefined;
    }
  }, [dispatch, setAccessToken]);

  /**
   * Effect listening for new access token
   * Sets timeout to refresh token based on the expiration time
   */
  useEffect(() => {
    const expiration = getTokenExpirationTime(accessToken);
    const timeout = setTimeout(
      () => refreshAccessToken(),
      expiration - REFRESH_TOKEN_EXPIRATION_BUFFER
    );

    return () => clearTimeout(timeout);
  }, [accessToken, refreshAccessToken]);

  return {
    refreshAccessToken
  };
};
